import ChannelFootageNotice from '../components/ChannelFootageNotice';
import VideoThumbnailField from '../components/VideoThumbnailField';
import VideoThumbnailRow from '../components/VideoThumbnailRow';
import VideoThumbnailService from '../services/VideoThumbnailService';
import { thumbnailPayload } from '../components/thumbnails/thumbnailFiles';
import ChannelYouTubeEligibilityNotice from '../components/ChannelYouTubeEligibilityNotice';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Tooltip,
  IconButton,
  Checkbox,
  FormControlLabel,
  Menu,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Add as AddIcon,
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Replay as ReplayIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import * as StoryScript from '../services/StoryScriptService';
import AuthService from '../services/AuthService';
import AiStoriesChannelService from '../services/AiStoriesChannelService';
import VoiceOverConfigService from '../services/VoiceOverConfigService';
import ChannelBadge from '../components/ChannelBadge';
import { surface } from '../theme';
import { AI_STORIES_CHIP_SX, BIKERS_DEFAULT_VOICE_ID } from './ai-stories/constants';
import AiStoriesPipelineStatusCell from './ai-stories/AiStoriesPipelineStatusCell';
import EditAiStoryDialog from './ai-stories/EditAiStoryDialog';
import UploadStoryDialog from './ai-stories/UploadStoryDialog';
import AddChannelDialog from './sleep-videos-v3/AddChannelDialog';
import DeleteVideoDialog from './sleep-videos-v3/DeleteVideoDialog';
import { copyToClipboard } from './sleep-videos-v3/utils';
import { pickRandomChannelColor } from '../utils/channelColors';
import MobileListCard from '../components/responsive/MobileListCard';
import { triggerBrowserDownload, videoDownloadFilename } from '../utils/download';

const SCRIPTS_PER_PAGE = 10;
const LIST_POLL_MS = 5000;
const LEGACY_NICHE_LABELS = {
  bikers: 'Bikers',
  'mafia-boss': 'Mafia Boss',
  millionaire: 'Millionaire',
};

function fallbackNicheLabel(value) {
  if (!value) return null;
  return LEGACY_NICHE_LABELS[value] ?? value;
}

function getFailedPipelineStage(row) {
  if (row?.status === 'script_failed') return 'script';
  if (row?.voiceOverGenerationStatus?.status === 'failed') return 'voiceOver';
  if (row?.renderStatus?.status === 'failed') return 'render';
  return null;
}

function getPipelineActionLabel(failedStage) {
  if (failedStage === 'script') return 'Retry script generation';
  if (failedStage === 'voiceOver') return 'Retry voice over generation';
  if (failedStage === 'render') return 'Retry video render';
  return 'Start script generation';
}

function uploadDisabledReason(row, isAdmin) {
  if (!isAdmin) return 'Only admins can upload to YouTube';
  if (!row?.youtube?.connected) return 'Connect a YouTube channel in Channels to upload';
  if (row.youtube.needsReconnect) return 'Reconnect this YouTube channel in Channels';
  if (row.youtube.uploadBlockedReason) return row.youtube.uploadBlockedReason;
  if (row.renderStatus?.status !== 'completed' || !row.hasRenderedVideo) return 'Wait for the video to finish rendering';
  const status = row.youtube.uploadStatus;
  if (status === 'completed') return 'Already uploaded to YouTube';
  if (status === 'uploading') return `Uploading to YouTube (${row.youtube.progress}%)`;
  if (status === 'queued' || status === 'retrying') return 'YouTube upload queued';
  if (status === 'failed' || status === 'review') return 'Upload needs attention. Open Channels, then YouTube, to review';
  return '';
}

function StoryScriptsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const isAdmin = AuthService.hasRole(['admin', 'superadmin']);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  const [filterChannel, setFilterChannel] = useState(null);
  const [filterChannels, setFilterChannels] = useState([]);
  const [filterChannelsLoading, setFilterChannelsLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createThumbnail, setCreateThumbnail] = useState(undefined);
  const [editThumbnail, setEditThumbnail] = useState(undefined);
  const [thumbnailBusy, setThumbnailBusy] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContext, setModalContext] = useState('');
  const [niche, setNiche] = useState('bikers');
  const [promptSystems, setPromptSystems] = useState([]);
  const [promptSystemsLoading, setPromptSystemsLoading] = useState(false);
  const [modalChannel, setModalChannel] = useState(null);
  const [modalVoiceConfig, setModalVoiceConfig] = useState(null);
  const [channelsForCreate, setChannelsForCreate] = useState([]);
  const [voiceConfigsForCreate, setVoiceConfigsForCreate] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [modalAutoUpload, setModalAutoUpload] = useState(false);
  const createUploadDisabledReason = !modalChannel
    ? 'Select a channel first.'
    : !modalChannel.youtubeConnected
      ? 'This channel is not connected to YouTube. Connect it in Channels.'
      : modalChannel.youtubeNeedsReconnect
        ? 'Reconnect this channel to YouTube in Channels.'
        : '';

  const [addChannelOpen, setAddChannelOpen] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelProfileNumber, setNewChannelProfileNumber] = useState('');
  const [addingChannel, setAddingChannel] = useState(false);
  const [addChannelError, setAddChannelError] = useState(null);

  const [togglingUsedId, setTogglingUsedId] = useState(null);
  const [runningPipelineActionIds, setRunningPipelineActionIds] = useState(() => new Set());
  const [copiedTitleId, setCopiedTitleId] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [editItem, setEditItem] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContext, setEditContext] = useState('');
  const [editChannel, setEditChannel] = useState(null);
  const [editVoiceConfig, setEditVoiceConfig] = useState(null);
  const [editChannels, setEditChannels] = useState([]);
  const [editChannelsLoading, setEditChannelsLoading] = useState(false);
  const [voiceConfigsForEdit, setVoiceConfigsForEdit] = useState([]);
  const [voiceConfigsEditLoading, setVoiceConfigsEditLoading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const [mobileMenuRow, setMobileMenuRow] = useState(null);
  const [uploadStory, setUploadStory] = useState(null);
  const [uploadNotice, setUploadNotice] = useState('');

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [filterChannel?.id]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, filterChannel?.id, debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPromptSystemsLoading(true);
        const data = await StoryScript.getPromptSystems();
        const items = Array.isArray(data.items) ? data.items : [];
        if (cancelled) return;
        setPromptSystems(items);
        setNiche((current) =>
          items.length && !items.some((item) => item.niche === current)
            ? items[0].niche
            : current,
        );
      } catch (err) {
        if (!cancelled) {
          setPromptSystems([]);
          setError(err.response?.data?.error || err.message || 'Failed to load prompt systems');
        }
      } finally {
        if (!cancelled) setPromptSystemsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const promptSystemByNiche = useMemo(
    () => new Map(promptSystems.map((item) => [item.niche, item])),
    [promptSystems],
  );

  const getNicheLabel = useCallback(
    (value) => promptSystemByNiche.get(value)?.label || fallbackNicheLabel(value),
    [promptSystemByNiche],
  );

  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const data = await StoryScript.listScripts({
        page,
        limit: SCRIPTS_PER_PAGE,
        search: debouncedSearch,
        ...(filterChannel?.id ? { channelId: filterChannel.id } : {}),
      });
      if (data.success) {
        setItems(data.scripts || []);
        setPagination(data.pagination);
      } else if (!silent) {
        setError(data.error || 'Failed to load scripts');
      }
    } catch (err) {
      if (!silent) setError(err.message || 'Failed to load scripts');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, debouncedSearch, filterChannel?.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!createOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [chList, voList] = await Promise.all([
          AiStoriesChannelService.list(),
          isAdmin ? VoiceOverConfigService.list('ai-stories') : Promise.resolve([]),
        ]);
        if (cancelled) return;
        setChannelsForCreate(Array.isArray(chList) ? chList : []);
        setVoiceConfigsForCreate(Array.isArray(voList) ? voList : []);
      } catch {
        if (!cancelled) {
          setChannelsForCreate([]);
          setVoiceConfigsForCreate([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [createOpen, isAdmin]);

  useEffect(() => {
    if (!createOpen || !isAdmin || !voiceConfigsForCreate.length) return;
    if (modalChannel?.voiceOverConfigId) {
      setModalVoiceConfig(null);
    } else if (niche === 'bikers') {
      const config = voiceConfigsForCreate.find((c) => c.voice_id === BIKERS_DEFAULT_VOICE_ID);
      if (config) setModalVoiceConfig(config);
    } else {
      setModalVoiceConfig(null);
    }
  }, [createOpen, isAdmin, niche, voiceConfigsForCreate, modalChannel?.voiceOverConfigId]);

  useEffect(() => {
    if (!editItem || !isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      setEditChannelsLoading(true);
      setVoiceConfigsEditLoading(true);
      try {
        const [chList, voList] = await Promise.all([
          AiStoriesChannelService.list(),
          VoiceOverConfigService.list('ai-stories'),
        ]);
        if (cancelled) return;
        const channels = Array.isArray(chList) ? chList : [];
        const vos = Array.isArray(voList) ? voList : [];
        setEditChannels(channels);
        setVoiceConfigsForEdit(vos);
        const data = await StoryScript.fetchScript(editItem.id);
        if (cancelled) return;
        if (data?.success && data.script) {
          const s = data.script;
          setEditContext(s.context ?? '');
          const vid = s.voiceOverConfigId;
          if (vid && vos.length) {
            const vc = vos.find((c) => String(c.id) === String(vid));
            setEditVoiceConfig(vc || null);
          } else {
            setEditVoiceConfig(null);
          }
        }
      } catch {
        if (!cancelled) {
          setEditChannels([]);
          setVoiceConfigsForEdit([]);
        }
      } finally {
        if (!cancelled) {
          setEditChannelsLoading(false);
          setVoiceConfigsEditLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editItem, isAdmin]);

  useEffect(() => {
    if (loading) return undefined;
    const id = setInterval(() => {
      load({ silent: true });
    }, LIST_POLL_MS);
    return () => clearInterval(id);
  }, [loading, load]);

  const handleCreate = async () => {
    if (createLoading || thumbnailBusy) return;
    if (!modalTitle.trim()) {
      setCreateError('Please enter a video title');
      return;
    }
    if (!modalChannel?.id) {
      setCreateError('Select a channel');
      return;
    }
    if (!modalChannel?.footageFolderId) {
      setCreateError('Configure a footage folder for this channel before creating a video.');
      return;
    }
    if (!niche) {
      setCreateError('Select a prompt system');
      return;
    }
    setCreateError('');
    setCreateLoading(true);
    try {
      const createOpts =
        (modalChannel?.id || modalVoiceConfig?.id)
          ? {
              ...(modalChannel?.id ? { channelId: modalChannel.id } : {}),
              ...(modalVoiceConfig?.id ? { voiceOverConfigId: modalVoiceConfig.id } : {}),
            }
          : {};
      const result = await StoryScript.createScript(
        modalTitle.trim(),
        niche,
        modalContext.trim(),
        {
          ...createOpts,
          thumbnail: await thumbnailPayload(createThumbnail),
          autoUploadToChannel: modalAutoUpload && !createUploadDisabledReason,
        },
      );
      if (result.success && result.scriptId) {
        setCreateOpen(false);
        setModalTitle('');
        setModalContext('');
        setModalChannel(null);
        setModalVoiceConfig(null);
        setModalAutoUpload(false);
        navigate(`/ai-stories/${result.scriptId}`);
      } else {
        setCreateError(result.message || 'Failed to create');
      }
    } catch (err) {
      setCreateError(err.response?.data?.error || err.message || 'Failed to create script');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleOpenAddChannel = () => {
    setNewChannelName('');
    setNewChannelProfileNumber('');
    setAddChannelError(null);
    setAddChannelOpen(true);
  };

  const handleCloseAddChannel = () => {
    if (!addingChannel) {
      setAddChannelOpen(false);
      setNewChannelName('');
      setNewChannelProfileNumber('');
      setAddChannelError(null);
    }
  };

  const handleAddChannel = async () => {
    const name = newChannelName?.trim();
    if (!name) {
      setAddChannelError('Channel name is required');
      return;
    }
    const num = parseInt(newChannelProfileNumber, 10);
    if (Number.isNaN(num) || num < 1) {
      setAddChannelError('Profile number must be an integer greater than 0');
      return;
    }
    try {
      setAddingChannel(true);
      setAddChannelError(null);
      const created = await AiStoriesChannelService.create({
        name,
        profileNumber: num,
        color: pickRandomChannelColor(),
      });
      setChannelsForCreate((prev) => [...prev, created]);
      setEditChannels((prev) => [...prev, created]);
      if (editItem) {
        setEditChannel(created);
      } else {
        setModalChannel(created);
      }
      setAddChannelOpen(false);
      setNewChannelName('');
      setNewChannelProfileNumber('');
    } catch (err) {
      setAddChannelError(err.response?.data?.error || err.message || 'Failed to create channel');
    } finally {
      setAddingChannel(false);
    }
  };

  const handleOpenEdit = (row, e) => {
    e.stopPropagation();
    if (!isAdmin) {
      navigate(`/ai-stories/${row.id}`);
      return;
    }
    setEditItem(row);
    setEditThumbnail(undefined);
    setThumbnailBusy(false);
    setEditTitle(row.novelTitle || '');
    setEditContext('');
    setEditChannel(
      row.channel
        ? {
            id: row.channel.id,
            name: row.channel.name,
            color: row.channel.color ?? '',
            profileNumber: row.channel.profileNumber,
          }
        : null,
    );
    setEditVoiceConfig(null);
    setEditError(null);
  };

  const handleGenerateScript = async (row, event) => {
    event?.stopPropagation();
    if (!row || runningPipelineActionIds.has(row.id)) return;

    setError(null);
    setRunningPipelineActionIds((current) => new Set(current).add(row.id));
    setItems((current) =>
      current.map((item) =>
        item.id === row.id
          ? { ...item, status: 'queued', scriptGenerationError: '' }
          : item,
      ),
    );

    try {
      await StoryScript.selectPlotAndGenerate(
        String(row.selectedPlot || '').trim(),
        row.novelTitle,
        row.niche,
        row.id,
      );
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          `Failed to generate ${row.novelTitle || 'story'}`,
      );
    } finally {
      setRunningPipelineActionIds((current) => {
        const next = new Set(current);
        next.delete(row.id);
        return next;
      });
      await load({ silent: true });
    }
  };

  const handlePipelineAction = async (row, event) => {
    event?.stopPropagation();
    if (!row || runningPipelineActionIds.has(row.id)) return;

    const failedStage = getFailedPipelineStage(row);
    if (!failedStage || failedStage === 'script') {
      await handleGenerateScript(row, event);
      return;
    }

    setError(null);
    setRunningPipelineActionIds((current) => new Set(current).add(row.id));
    setItems((current) =>
      current.map((item) => {
        if (item.id !== row.id) return item;
        if (failedStage === 'voiceOver') {
          return {
            ...item,
            voiceOverGenerationStatus: { status: 'queued' },
          };
        }
        return {
          ...item,
          renderStatus: { ...item.renderStatus, status: 'queued' },
        };
      }),
    );

    try {
      if (failedStage === 'voiceOver') {
        await StoryScript.generateVoiceOver(row.id);
      } else {
        await StoryScript.renderScriptVideo(row.id);
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          (failedStage === 'voiceOver'
            ? 'Failed to retry voice over generation'
            : 'Failed to retry video render'),
      );
    } finally {
      setRunningPipelineActionIds((current) => {
        const next = new Set(current);
        next.delete(row.id);
        return next;
      });
      await load({ silent: true });
    }
  };

  const handleCloseEdit = () => {
    if (!savingEdit && !thumbnailBusy) {
      setEditItem(null);
      setEditError(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editItem || savingEdit || thumbnailBusy) return;
    const title = editTitle?.trim();
    if (!title) {
      setEditError('Title is required');
      return;
    }
    try {
      setSavingEdit(true);
      setEditError(null);
      await StoryScript.updateScript(editItem.id, {
        novelTitle: title,
        thumbnail: await thumbnailPayload(editThumbnail),
        context: editContext?.trim() || '',
        channelId: editChannel?.id ?? null,
        voiceOverConfigId: editVoiceConfig?.id ?? null,
      });
      VideoThumbnailService.notifyChanged('ai-stories', editItem.id);
      setEditItem(null);
      await load();
    } catch (err) {
      setEditError(
        err.response?.data?.error || err.message || 'Failed to update story',
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const handleStoryDeleteClick = (row, e) => {
    e.stopPropagation();
    if (!isAdmin) return;
    if (selectedIds.size > 0 && selectedIds.has(row.id)) {
      const payloadItems = items
        .filter((i) => selectedIds.has(i.id))
        .map((i) => ({ id: i.id, title: i.novelTitle || 'Story' }));
      setDeleteItem({ items: payloadItems });
    } else {
      setDeleteItem({ id: row.id, title: row.novelTitle || 'Story' });
    }
  };

  const handleCloseDelete = () => {
    if (!deleting) setDeleteItem(null);
  };
  const openMobileMenu = (event, row) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(event.currentTarget);
    setMobileMenuRow(row);
  };
  const closeMobileMenu = () => {
    setMobileMenuAnchorEl(null);
    setMobileMenuRow(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem) return;
    try {
      setDeleting(true);
      setError(null);
      if (deleteItem.items) {
        const results = await Promise.allSettled(
          deleteItem.items.map((it) => StoryScript.deleteScript(it.id)),
        );
        const failed = results.filter((r) => r.status === 'rejected');
        if (failed.length > 0) {
          const first = failed[0].reason;
          const msg = first?.response?.data?.error || first?.message || 'Failed to delete';
          setError(
            failed.length === deleteItem.items.length
              ? msg
              : `${deleteItem.items.length - failed.length} deleted; ${failed.length} failed: ${msg}`,
          );
        }
      } else {
        await StoryScript.deleteScript(deleteItem.id);
      }
      setDeleteItem(null);
      setSelectedIds(new Set());
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete script');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Typography
        variant='h4'
        sx={{
          fontWeight: 700,
          color: 'white',
          mb: 3,
          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        AI Stories
      </Typography>

      <Paper sx={{ borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {uploadNotice && (
            <Alert severity='success' sx={{ mb: 2 }} onClose={() => setUploadNotice('')}>
              {uploadNotice}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={() => {
                setCreateOpen(true);
                setCreateThumbnail(undefined);
                setThumbnailBusy(false);
                setCreateError('');
                setModalAutoUpload(false);
                setModalVoiceConfig(null);
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 2,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              New Video
            </Button>
            {isAdmin && (
              <Button
                variant='outlined'
                startIcon={<RecordVoiceOverIcon />}
                onClick={() => navigate('/ai-stories/voice-over-configs')}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}
              >
                Voice Over
              </Button>
            )}
            {isAdmin && (
              <Button
                variant='outlined'
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/ai-stories/prompt-system/edit')}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}
              >
                Prompt System
              </Button>
            )}
            <Autocomplete
              sx={{ minWidth: 0, width: { xs: '100%', sm: 320 } }}
              options={filterChannels}
              getOptionLabel={(opt) =>
                (opt && (opt.profileNumber != null ? `${opt.name} (#${opt.profileNumber})` : opt.name)) || ''
              }
              value={filterChannel}
              onChange={(_, val) => setFilterChannel(val)}
              loading={filterChannelsLoading}
              filterOptions={(x) => x}
              onOpen={() => {
                setFilterChannelsLoading(true);
                AiStoriesChannelService.list('')
                  .then((list) => setFilterChannels(Array.isArray(list) ? list : []))
                  .finally(() => setFilterChannelsLoading(false));
              }}
              onInputChange={(_, value) => {
                setFilterChannelsLoading(true);
                AiStoriesChannelService.list(value || '')
                  .then((list) => setFilterChannels(Array.isArray(list) ? list : []))
                  .finally(() => setFilterChannelsLoading(false));
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <ChannelBadge
                    channel={{
                      ...option,
                      name:
                        option.profileNumber != null
                          ? `${option.name} (#${option.profileNumber})`
                          : option.name,
                    }}
                  />
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} size='small' label='Filter by channel' placeholder='All channels' />
              )}
            />
            <TextField
              size='small'
              placeholder='Search by title'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 0, width: { xs: '100%', sm: 280 }, flex: '1 1 220px', maxWidth: { xs: '100%', sm: 400 } }}
            />
          </Box>

          {loading ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Typography color='text.secondary' sx={{ py: 3 }}>
              No scripts yet. Click &quot;New Video&quot; to create one.
            </Typography>
          ) : isMobile ? (
            <>
              {items.map((row) => {
                const nicheText = getNicheLabel(row.niche);
                const canStartScript = ['creating', 'plots_generated'].includes(row.status);
                const failedStage = getFailedPipelineStage(row);
                const pipelineActionRunning = runningPipelineActionIds.has(row.id);
                return (
                  <VideoThumbnailRow
                    component={MobileListCard}
                    kind='ai-stories' videoId={row.id} videoTitle={row.novelTitle} enabled={isAdmin}
                    key={row.id}
                    title={row.novelTitle}
                    subtitle={row.channel?.name || 'No channel'}
                    meta={row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '—'}
                    onClick={() => navigate(`/ai-stories/${row.id}`)}
                    primaryAction={
                      <Button
                        size='small'
                        color={failedStage ? 'error' : 'primary'}
                        disabled={
                          pipelineActionRunning || (!canStartScript && !failedStage)
                        }
                        startIcon={
                          pipelineActionRunning ? (
                            <CircularProgress size={14} color='inherit' />
                          ) : failedStage ? (
                            <ReplayIcon fontSize='small' />
                          ) : canStartScript ? (
                            <PlayArrowIcon fontSize='small' />
                          ) : null
                        }
                        onClick={(event) => handlePipelineAction(row, event)}
                        sx={{ minWidth: 0, px: 1 }}
                      >
                        {pipelineActionRunning ? (failedStage ? 'Retrying' : 'Starting') : failedStage ? 'Retry' : 'Start'}
                      </Button>
                    }
                    secondaryAction={
                      <IconButton size='small' onClick={(e) => openMobileMenu(e, row)}>
                        <MoreVertIcon fontSize='small' />
                      </IconButton>
                    }
                    statusSummary={
                      <AiStoriesPipelineStatusCell row={row} now={Date.now()} />
                    }
                  >
                    {nicheText ? (
                      <Chip label={nicheText} size='small' variant='outlined' color='default' sx={AI_STORIES_CHIP_SX} />
                    ) : null}
                  </VideoThumbnailRow>
                );
              })}
              <Menu
                anchorEl={mobileMenuAnchorEl}
                open={Boolean(mobileMenuAnchorEl)}
                onClose={closeMobileMenu}
              >
                <MenuItem
                  disabled={
                    !mobileMenuRow ||
                    runningPipelineActionIds.has(mobileMenuRow.id) ||
                    (!['creating', 'plots_generated'].includes(mobileMenuRow.status) &&
                      !getFailedPipelineStage(mobileMenuRow))
                  }
                  onClick={(event) => {
                    if (!mobileMenuRow) return;
                    const row = mobileMenuRow;
                    closeMobileMenu();
                    handlePipelineAction(row, event);
                  }}
                >
                  {getPipelineActionLabel(getFailedPipelineStage(mobileMenuRow))}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (mobileMenuRow) navigate(`/ai-stories/${mobileMenuRow.id}`);
                    closeMobileMenu();
                  }}
                >
                  Open details
                </MenuItem>
                {isAdmin && (
                  <MenuItem
                    onClick={() => {
                      if (!mobileMenuRow) return;
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(mobileMenuRow.id)) next.delete(mobileMenuRow.id);
                        else next.add(mobileMenuRow.id);
                        return next;
                      });
                      closeMobileMenu();
                    }}
                  >
                    Toggle selection
                  </MenuItem>
                )}
                <MenuItem
                  disabled={!mobileMenuRow || !isAdmin || togglingUsedId === mobileMenuRow.id}
                  onClick={async () => {
                    if (!mobileMenuRow || !isAdmin) return;
                    closeMobileMenu();
                    setTogglingUsedId(mobileMenuRow.id);
                    try {
                      await StoryScript.updateScript(mobileMenuRow.id, { used: !(mobileMenuRow.used ?? false) });
                      setItems((prev) =>
                        prev.map((it) => (it.id === mobileMenuRow.id ? { ...it, used: !(it.used ?? false) } : it)),
                      );
                    } finally {
                      setTogglingUsedId(null);
                    }
                  }}
                >
                  {(mobileMenuRow?.used ?? false) ? 'Mark as unused' : 'Mark as used'}
                </MenuItem>
                <MenuItem
                  disabled={Boolean(uploadDisabledReason(mobileMenuRow, isAdmin))}
                  onClick={() => {
                    setUploadStory(mobileMenuRow);
                    closeMobileMenu();
                  }}
                >
                  <UploadIcon fontSize='small' sx={{ mr: 1 }} />
                  {uploadDisabledReason(mobileMenuRow, isAdmin) || 'Upload to YouTube'}
                </MenuItem>
                <MenuItem
                  disabled={!mobileMenuRow}
                  onClick={async () => {
                    if (!mobileMenuRow) return;
                    closeMobileMenu();
                    const renderDone = mobileMenuRow.renderStatus?.status === 'completed';
                    const canDownloadItem = renderDone || Boolean(mobileMenuRow.voiceOverObjectName);
                    if (!canDownloadItem) return;
                    try {
                      const data = await StoryScript.fetchScript(mobileMenuRow.id);
                      const s = data?.script;
                      if (!s) return;
                      const url = renderDone && s.videoUrl ? s.videoUrl : s.voiceOverUrl;
                      if (!url) return;
                      triggerBrowserDownload(
                        url,
                        videoDownloadFilename(
                          s.channel?.name,
                          s.novelTitle,
                          renderDone && s.videoUrl ? '.mp4' : '.mp3',
                          'ai-story',
                        ),
                      );
                    } catch {
                      // ignore
                    }
                  }}
                >
                  Download output
                </MenuItem>
                <MenuItem
                  onClick={(e) => {
                    if (mobileMenuRow) handleOpenEdit(mobileMenuRow, e);
                    closeMobileMenu();
                  }}
                >
                  Edit
                </MenuItem>
                {isAdmin && (
                  <MenuItem
                    sx={{ color: 'error.main' }}
                    onClick={(e) => {
                      if (mobileMenuRow) handleStoryDeleteClick(mobileMenuRow, e);
                      closeMobileMenu();
                    }}
                  >
                    Delete
                  </MenuItem>
                )}
              </Menu>
              {pagination && pagination.totalCount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color='primary'
                    size='small'
                  />
                </Box>
              )}
            </>
          ) : (
            <>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      {isAdmin && (
                        <TableCell padding='checkbox' sx={{ fontWeight: 700 }}>
                          <Checkbox
                            size='small'
                            indeterminate={
                              items.some((r) => selectedIds.has(r.id)) &&
                              !items.every((r) => selectedIds.has(r.id))
                            }
                            checked={
                              items.length > 0 && items.every((r) => selectedIds.has(r.id))
                            }
                            onChange={() => {
                              const ids = items.map((r) => r.id);
                              setSelectedIds((prev) => {
                                const allSelected =
                                  ids.length > 0 && ids.every((id) => prev.has(id));
                                const next = new Set(prev);
                                if (allSelected) {
                                  ids.forEach((id) => next.delete(id));
                                } else {
                                  ids.forEach((id) => next.add(id));
                                }
                                return next;
                              });
                            }}
                            inputProps={{ 'aria-label': 'Select all on this page' }}
                          />
                        </TableCell>
                      )}
                      {isAdmin && <TableCell sx={{ fontWeight: 700, width: 88 }}>Thumbnail</TableCell>}
                      <TableCell sx={{ fontWeight: 700, width: '22%', minWidth: 180, maxWidth: 280 }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 700, maxWidth: 140 }}>Channel</TableCell>
                      <TableCell sx={{ fontWeight: 700, maxWidth: 160 }}>Niche</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '30%', minWidth: 380 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '1%', whiteSpace: 'nowrap' }}>Updated</TableCell>
                      <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((row) => {
                      const nicheText = getNicheLabel(row.niche);
                      const renderComplete = row.renderStatus?.status === 'completed';
                      const hasVoiceOver = Boolean(row.voiceOverObjectName);
                      const canDownload = renderComplete || hasVoiceOver;
                      const canStartScript = ['creating', 'plots_generated'].includes(row.status);
                      const failedStage = getFailedPipelineStage(row);
                      const pipelineActionRunning = runningPipelineActionIds.has(row.id);
                      const pipelineActionLabel = getPipelineActionLabel(failedStage);
                      return (
                        <VideoThumbnailRow
                          kind='ai-stories' videoId={row.id} videoTitle={row.novelTitle} enabled={isAdmin}
                          key={row.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            ...((row.used ?? false) && {
                              opacity: 0.6,
                              bgcolor: 'grey.300',
                              '& .MuiTableCell-root': {
                                textDecoration: 'line-through',
                                color: 'text.secondary',
                              },
                            }),
                          }}
                          onClick={() => navigate(`/ai-stories/${row.id}`)}
                        >
                          {isAdmin && (
                            <TableCell
                              padding='checkbox'
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                size='small'
                                checked={selectedIds.has(row.id)}
                                onChange={() => {
                                  setSelectedIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(row.id)) next.delete(row.id);
                                    else next.add(row.id);
                                    return next;
                                  });
                                }}
                                inputProps={{ 'aria-label': `Select ${row.novelTitle || 'story'}` }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ width: '22%', minWidth: 180, maxWidth: 280 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                component='span'
                                variant='body2'
                                title={row.novelTitle || ''}
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {row.novelTitle}
                              </Typography>
                              <Tooltip title={copiedTitleId === row.id ? 'Copied!' : 'Copy title'}>
                                <IconButton
                                  size='small'
                                  aria-label='Copy title'
                                  sx={{ flexShrink: 0, textDecoration: 'none' }}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const ok = await copyToClipboard(row.novelTitle || '');
                                    if (ok) {
                                      setCopiedTitleId(row.id);
                                      setTimeout(() => setCopiedTitleId(null), 2000);
                                    }
                                  }}
                                >
                                  {copiedTitleId === row.id ? (
                                    <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                  ) : (
                                    <ContentCopyIcon sx={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 140 }} onClick={(e) => e.stopPropagation()}>
                            {row.channel ? (
                              <ChannelBadge channel={row.channel} size='small' />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 160 }}>
                            {nicheText ? (
                              <Chip
                                label={nicheText}
                                size='small'
                                variant='outlined'
                                color='default'
                                sx={AI_STORIES_CHIP_SX}
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell sx={{ width: '30%', minWidth: 380 }}>
                            <AiStoriesPipelineStatusCell row={row} now={Date.now()} />
                          </TableCell>
                          <TableCell sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                            {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '—'}
                          </TableCell>
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                            sx={{ textAlign: 'center', verticalAlign: 'middle' }}
                          >
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                flexWrap: 'nowrap',
                              }}
                            >
                              {isAdmin &&
                              selectedIds.size > 0 &&
                              selectedIds.has(row.id) ? (
                                <Tooltip
                                  title={
                                    selectedIds.size > 1
                                      ? `Delete ${selectedIds.size} selected stories`
                                      : 'Delete'
                                  }
                                >
                                  <IconButton
                                    size='small'
                                    color='error'
                                    onClick={(e) => handleStoryDeleteClick(row, e)}
                                  >
                                    <DeleteIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <>
                                  <Tooltip
                                    title={
                                      pipelineActionRunning
                                        ? failedStage
                                          ? `Retrying ${pipelineActionLabel.slice(6).toLowerCase()}`
                                          : 'Starting script generation'
                                        : failedStage
                                          ? pipelineActionLabel
                                          : canStartScript
                                            ? 'Start script generation'
                                            : row.status === 'completed'
                                              ? 'Script generation completed'
                                              : 'Script generation in progress'
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size='small'
                                        color={failedStage ? 'error' : 'primary'}
                                        disabled={
                                          pipelineActionRunning ||
                                          (!canStartScript && !failedStage)
                                        }
                                        aria-label={pipelineActionLabel}
                                        onClick={(event) =>
                                          handlePipelineAction(row, event)
                                        }
                                      >
                                        {pipelineActionRunning ? (
                                          <CircularProgress size={18} color='inherit' />
                                        ) : failedStage ? (
                                          <ReplayIcon fontSize='small' />
                                        ) : (
                                          <PlayArrowIcon fontSize='small' />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip
                                    title={
                                      !isAdmin
                                        ? 'Only admins can mark used'
                                        : (row.used ?? false)
                                          ? 'Mark as unused'
                                          : 'Mark as used'
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size='small'
                                        disabled={!isAdmin || togglingUsedId === row.id}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (!isAdmin) return;
                                          setTogglingUsedId(row.id);
                                          try {
                                            await StoryScript.updateScript(row.id, {
                                              used: !(row.used ?? false),
                                            });
                                            setItems((prev) =>
                                              prev.map((it) =>
                                                it.id === row.id
                                                  ? { ...it, used: !(it.used ?? false) }
                                                  : it,
                                              ),
                                            );
                                          } catch {
                                            // ignore
                                          } finally {
                                            setTogglingUsedId(null);
                                          }
                                        }}
                                        color={(row.used ?? false) ? 'success' : 'default'}
                                      >
                                        {(row.used ?? false) ? (
                                          <CheckCircleIcon fontSize='small' />
                                        ) : (
                                          <CheckCircleOutlineIcon fontSize='small' />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title={uploadDisabledReason(row, isAdmin) || 'Upload to YouTube'}>
                                    <span>
                                      <IconButton
                                        size='small'
                                        aria-label='Upload to YouTube'
                                        disabled={Boolean(uploadDisabledReason(row, isAdmin))}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setUploadStory(row);
                                        }}
                                      >
                                        <UploadIcon fontSize='small' />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip
                                    title={
                                      !canDownload
                                        ? 'No video or voice over yet'
                                        : renderComplete
                                          ? 'Download video'
                                          : 'Download voice over'
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size='small'
                                        disabled={!canDownload}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (!canDownload) return;
                                          try {
                                            const data = await StoryScript.fetchScript(row.id);
                                            const s = data?.script;
                                            if (!s) return;
                                            const url =
                                              renderComplete && s.videoUrl
                                                ? s.videoUrl
                                                : s.voiceOverUrl;
                                            if (!url) return;
                                            const ext =
                                              renderComplete && s.videoUrl ? '.mp4' : '.mp3';
                                            triggerBrowserDownload(
                                              url,
                                              videoDownloadFilename(s.channel?.name, s.novelTitle, ext, 'ai-story'),
                                            );
                                          } catch {
                                            // ignore
                                          }
                                        }}
                                      >
                                        <DownloadIcon fontSize='small' />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip
                                    title={isAdmin ? 'Edit' : 'Open story details'}
                                  >
                                    <IconButton
                                      size='small'
                                      onClick={(e) => handleOpenEdit(row, e)}
                                    >
                                      <EditIcon fontSize='small' />
                                    </IconButton>
                                  </Tooltip>
                                  {isAdmin && (
                                    <Tooltip title='Delete'>
                                      <IconButton
                                        size='small'
                                        onClick={(e) => handleStoryDeleteClick(row, e)}
                                      >
                                        <DeleteIcon fontSize='small' />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </VideoThumbnailRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {pagination && pagination.totalCount > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    {(page - 1) * SCRIPTS_PER_PAGE + 1}–
                    {Math.min(page * SCRIPTS_PER_PAGE, pagination.totalCount)} of{' '}
                    {pagination.totalCount}
                  </Typography>
                  <Pagination
                    count={pagination.totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color='primary'
                    size='small'
                    siblingCount={5}
                    boundaryCount={1}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>

      {uploadStory && (
        <UploadStoryDialog
          story={uploadStory}
          onClose={() => setUploadStory(null)}
          onUploaded={(upload) => {
            setItems((current) => current.map((row) => row.id === uploadStory.id
              ? { ...row, youtube: { ...row.youtube, uploadStatus: upload.status } }
              : row));
            setUploadStory(null);
            setUploadNotice(upload.status === 'completed'
              ? 'This video is already uploaded to YouTube.'
              : 'YouTube upload queued. You can track progress in Channels > YouTube.');
            load({ silent: true });
          }}
        />
      )}

      <Dialog open={createOpen} onClose={() => !createLoading && !thumbnailBusy && setCreateOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>New AI story</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Video title'
            fullWidth
            value={modalTitle}
            onChange={(e) => {
              setModalTitle(e.target.value);
              setCreateError('');
            }}
            sx={{ mb: 2 }}
          />
          {isAdmin && (
            <VideoThumbnailField
              kind='ai-stories'
              title={modalTitle}
              value={createThumbnail}
              onChange={setCreateThumbnail}
              disabled={createLoading || thumbnailBusy}
              onBusyChange={setThumbnailBusy}
            />
          )}
          <TextField
            margin='dense'
            label='Context (optional)'
            fullWidth
            multiline
            minRows={3}
            value={modalContext}
            onChange={(e) => setModalContext(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            label='Prompt system'
            fullWidth
            value={promptSystems.some((item) => item.niche === niche) ? niche : ''}
            onChange={(e) => setNiche(e.target.value)}
            disabled={promptSystemsLoading || !promptSystems.length}
            helperText={!promptSystemsLoading && !promptSystems.length ? 'No prompt systems are available.' : ''}
            sx={{ mb: 2 }}
          >
            {promptSystems.map((o) => (
              <MenuItem key={o.niche} value={o.niche}>
                {o.label || fallbackNicheLabel(o.niche)}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title={createUploadDisabledReason || 'Upload the finished video as private to this channel.'}>
            <Box component='span' tabIndex={createUploadDisabledReason ? 0 : undefined} sx={{ display: 'block', mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={modalAutoUpload && !createUploadDisabledReason}
                    disabled={Boolean(createUploadDisabledReason)}
                    onChange={(_, checked) => setModalAutoUpload(checked)}
                  />
                }
                label='Auto upload to channel'
                sx={{ ml: 0 }}
              />
            </Box>
          </Tooltip>
          <>
              <Autocomplete
                options={channelsForCreate}
                getOptionLabel={(o) =>
                  o ? `${o.name} (profile ${o.profileNumber ?? '—'})` : ''
                }
                value={modalChannel}
                onChange={(_, channel) => {
                  setModalChannel(channel);
                  setModalAutoUpload(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Channel'
                    required
                    margin='dense'
                    placeholder='Select a channel'
                  />
                )}
                sx={{ mb: 2 }}
              />
              {isAdmin && <Box sx={{ mt: 1, mb: 2 }}>
                <Button size='small' onClick={handleOpenAddChannel}>
                  Add new channel
                </Button>
              </Box>}
              <Autocomplete
                options={voiceConfigsForCreate}
                getOptionLabel={(o) => (o ? o.name || o.voice_id || o.id : '')}
                value={modalVoiceConfig}
                onChange={(_, v) => setModalVoiceConfig(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Voice over config (optional)'
                    margin='dense'
                    placeholder='Auto TTS after script completes'
                    helperText={modalChannel?.voiceOverConfigId ? 'Leave empty to use this channel’s default voice.' : ''}
                  />
                )}
              />
          </>
          <ChannelFootageNotice channel={modalChannel} kind="ai-stories" />
          {isAdmin && createOpen && (
            <ChannelYouTubeEligibilityNotice channel={modalChannel} kind="ai-stories" />
          )}
          {createError && (
            <Alert severity='error' sx={{ mt: 2 }}>
              {createError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={createLoading || thumbnailBusy}>
            Cancel
          </Button>
          <Button onClick={handleCreate} variant='contained' disabled={createLoading || thumbnailBusy}>
            {createLoading ? <CircularProgress size={22} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddChannelDialog
        open={addChannelOpen}
        onClose={handleCloseAddChannel}
        addingChannel={addingChannel}
        addChannelError={addChannelError}
        newChannelName={newChannelName}
        setNewChannelName={setNewChannelName}
        newChannelProfileNumber={newChannelProfileNumber}
        setNewChannelProfileNumber={setNewChannelProfileNumber}
        handleAddChannel={handleAddChannel}
      />

      <EditAiStoryDialog
        open={Boolean(editItem)}
        onClose={handleCloseEdit}
        editItem={editItem}
        editTitle={editTitle}
        setEditTitle={setEditTitle}
        editContext={editContext}
        setEditContext={setEditContext}
        editChannel={editChannel}
        setEditChannel={setEditChannel}
        editVoiceConfig={editVoiceConfig}
        setEditVoiceConfig={setEditVoiceConfig}
        editChannels={editChannels}
        editChannelsLoading={editChannelsLoading}
        voiceConfigsForEdit={voiceConfigsForEdit}
        voiceConfigsLoading={voiceConfigsEditLoading}
        editError={editError}
        setEditError={setEditError}
        handleSaveEdit={handleSaveEdit}
        savingEdit={savingEdit}
        thumbnail={editThumbnail}
        setThumbnail={setEditThumbnail}
        thumbnailBusy={thumbnailBusy}
        setThumbnailBusy={setThumbnailBusy}
        onOpenAddChannel={handleOpenAddChannel}
      />

      <DeleteVideoDialog
        deleteItem={deleteItem}
        deleting={deleting}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        resourceSingular='story'
        resourcePlural='stories'
      />
    </Box>
  );
}

export default StoryScriptsPage;
